import { TinyVariant } from '@shared/constants/components/tinyUtils';
import { SkillModel } from '@shared/models/skills/SkillModel';
import type { TFunction } from 'i18next';

/**
 * How an installed skill is used, flattening `enabled` + `activation` into one choice: Off (never
 * used), Auto (the assistant may pick it), or Manual (only when attached with / or @).
 *
 * The MCP equivalent is `mcpMode.ts`; the two stay deliberately parallel so a tool and a skill read
 * the same way on a card and in a detail dialog.
 */
export type SkillMode = 'off' | 'auto' | 'manual';

/** The single mode a skill is currently in. */
export function skillModeFor(skill: SkillModel): SkillMode {
    return !skill.enabled ? 'off' : skill.activation;
}

/** Update payload for switching a skill to `mode` — Off pauses it, the rest set the activation. */
export function skillModeUpdate(skillId: string, mode: SkillMode) {
    return mode === 'off' ? { id: skillId, enabled: false } : { id: skillId, enabled: true, activation: mode };
}

export interface SkillModeOption {
    value: SkillMode;
    label: string;
    description: string;
}

/** The three mode choices, in order, with the wording used by both the card label and the toggle. */
export function skillModeOptions(t: TFunction<'common'>): SkillModeOption[] {
    return [
        {
            value: 'off',
            label: t('skills.mode.off'),
            description: t('skills.mode.offTooltip'),
        },
        {
            value: 'auto',
            label: t('skills.activation.autoShort'),
            description: t('skills.activation.autoDescription'),
        },
        {
            value: 'manual',
            label: t('skills.activation.manualShort'),
            description: t('skills.activation.manualDescription'),
        },
    ];
}

/**
 * How the mode reads as a single tag on a card: a paused skill is muted, an automatic one takes the
 * accent, and a manual one the secondary colour — distinct at a glance without a legend.
 */
export function skillModeTagVariant(mode: SkillMode): TinyVariant {
    if (mode === 'auto') return 'success';
    if (mode === 'manual') return 'accent';
    return 'diminished';
}
